/**
 * Brand theme for the Console.
 *
 * Every colour comes from config/palette.js, Lynear's palette and what the
 * Console derives from it - the one place a colour is set. The brand gold is
 * the one LIV's web app uses too (#AF9C65), so the Console still reads as the
 * same product inside the LIV shell.
 */
import { GOLD, LYNEAR_SECONDARY, NEUTRAL, PRESET_SEEDS, TONE } from './palette.js';

export const BRAND_PRIMARY = GOLD.base;
export const THEME_PRIMARY_DARK = GOLD.dark;
export const THEME_PRIMARY = BRAND_PRIMARY;

/** Semantic palette, for the code that still reads it by these names. */
export const PALETTE = Object.freeze({
    white: NEUTRAL.white,
    primary: BRAND_PRIMARY,
    primaryDark: THEME_PRIMARY_DARK,
    primaryShade: 'rgba(175,156,101,0.1)',
    primaryWo: 'rgba(175,156,101,.15)',
    yellow: TONE.warning.strong,
    red: TONE.danger.text,
    redLight: TONE.danger.strong,
    green: TONE.success.text,
    black: NEUTRAL.text,
    gray: NEUTRAL.surface,
    lightGrey: NEUTRAL.line,
    disabledGrey: NEUTRAL.line
});

/**
 * The Console's own antd class prefix (spec §36.2.1). LIV's global antd overrides are keyed to
 * `ant-` and `lgim-` classes; a second antd instance under its own prefix collides with neither.
 */
export const ANTD_PREFIX = 'lfoc';

/**
 * Ant Design theme token. Mirrors the web app's ConfigProvider config, including the Select
 * component tokens it sets so dropdowns highlight in brand gold rather than antd blue.
 */
export const antdTheme = {
    token: {
        colorPrimary: THEME_PRIMARY,
        colorPrimaryHover: THEME_PRIMARY_DARK,
        colorPrimaryActive: THEME_PRIMARY_DARK,
        colorLink: THEME_PRIMARY,
        colorLinkHover: THEME_PRIMARY_DARK,
        colorLinkActive: THEME_PRIMARY_DARK,
        // The status colours antd's own components use (Alert, Badge status, message, Tag success/error/warning/processing).
        // The palette's own colours: antd derives its light backgrounds and hovers from them.
        colorSuccess: LYNEAR_SECONDARY.oliveGreen,
        colorError: LYNEAR_SECONDARY.deepMaroon,
        colorWarning: LYNEAR_SECONDARY.goldenOchre,
        colorInfo: LYNEAR_SECONDARY.tealOcean,
        colorText: NEUTRAL.text,
        colorTextSecondary: NEUTRAL.muted,
        colorBorder: NEUTRAL.line,
        colorBorderSecondary: NEUTRAL.line,
        colorBgLayout: NEUTRAL.surface,
        // Every preset tag colour (<Tag color="green">, "gold", "blue"...) seeded from the palette (palette.js PRESET_SEEDS).
        ...PRESET_SEEDS,
        fontFamily: "'Roboto', sans-serif"
    },
    components: {
        Select: {
            optionSelectedBg: THEME_PRIMARY,
            optionSelectedColor: NEUTRAL.white,
            optionActiveBg: GOLD.tint
        }
    }
};
